import { useMemo, useState } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { Button, Icon, Text, theme, TogglePillSet, type PillOption } from '@socrait/react';
import { marked } from 'marked';
import RenderHtml from 'react-native-render-html';

import { Loading } from '~/components/Loading';
import { AccordionBase, useAccordionState } from '~/components/Accordion';
import { ErrorBox } from '~/components/ErrorBox';
import { useRenderHtmlConfig } from '~/hooks/useRenderHtmlConfig';
import { useReviewClassSummary } from '~/services/review/useReviewClassSummaryQuery';
import { getMeetingContentState } from '~/pages/review/utils/meetingContentState';

import { BaseActionItemProps } from './ActionItemTypes';

const ALL_SUBJECTS = 'all';

export const ClassSummaryActionItem = ({
  title,
  message,
  date,
  periodId,
  meetingId,
}: BaseActionItemProps & { periodId: string }) => {
  const { isCollapsed, handleToggleCollapse, handleReviewButtonClick } = useAccordionState();
  const { data, status, error } = useReviewClassSummary({
    date,
    periodId: periodId,
    enabled: true,
  });
  const [selectedBlockId, setSelectedBlockId] = useState<string>(ALL_SUBJECTS);

  const contentState = getMeetingContentState(data?.meetings, meetingId, data?.status);
  const meeting = contentState.kind === 'content' ? contentState.meeting : undefined;
  const blocks = meeting?.blocks ?? [];
  const showPills = blocks.length >= 2;
  const effectiveBlockId =
    selectedBlockId === ALL_SUBJECTS || blocks.some((block) => block.blockId === selectedBlockId)
      ? selectedBlockId
      : ALL_SUBJECTS;

  const activeMarkdown =
    effectiveBlockId === ALL_SUBJECTS
      ? meeting?.markdown
      : blocks.find((b) => b.blockId === effectiveBlockId)?.markdown;

  const pillOptions: PillOption[] = [
    { label: 'All Subjects', value: ALL_SUBJECTS },
    ...blocks.map((block) => ({ label: block.subject ?? 'General', value: block.blockId })),
  ];

  // Parse markdown to HTML when data is available using useMemo
  const summaryHtml = useMemo(() => {
    if (activeMarkdown) {
      return marked.parse(activeMarkdown) as string;
    }
    return '';
  }, [activeMarkdown]);

  const accordionState = status === 'pending' ? 'loading' : 'loaded';

  const reviewButton = (
    <Button
      name="Review"
      onPress={handleReviewButtonClick}
      containerStyle={styles.reviewButton}
      fontStyle={styles.reviewButtonText}
      variant="primary"
    />
  );

  const getCopyText = () => {
    const text = activeMarkdown || message || '';
    return {
      plain: text,
      html: summaryHtml || text,
    };
  };

  const { fonts, defaultTagStyles } = useRenderHtmlConfig();

  const tagsStyles = {
    ...defaultTagStyles,
    h1: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 18, marginBottom: 8 },
    h2: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 16, marginTop: 12, marginBottom: 4 },
    h3: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 15, marginTop: 8, marginBottom: 4 },
    p: { fontFamily: theme.fonts.regular, lineHeight: 20, marginBottom: 8 },
    li: { fontFamily: theme.fonts.regular, lineHeight: 20, marginLeft: 16 },
    ul: { marginLeft: 0 },
    ol: { marginLeft: 0 },
    strong: { fontFamily: theme.fonts.bold, fontWeight: 'bold' },
    em: { fontFamily: theme.fonts.regular, fontStyle: 'italic' },
  };

  const getContent = () => {
    if (error) {
      return (
        <ErrorBox text="Could not load class summary. Please try reopening this action item or reloading the window." />
      );
    }

    if (status === 'pending') {
      return (
        <View style={styles.loadingContainer}>
          <Loading backgroundColor={theme.colors.transparent} />
        </View>
      );
    }

    if (contentState.kind === 'no-meetings') {
      return (
        <Text style={styles.noDataText}>
          {contentState.dayStatus === 'processed' ? 'No class content found for this day.' : 'No data available'}
        </Text>
      );
    }

    if (contentState.kind === 'processing') {
      return (
        <View style={styles.loadingContainer}>
          <Loading backgroundColor={theme.colors.transparent} />
        </View>
      );
    }

    if (contentState.kind === 'failed') {
      return (
        <ErrorBox
          text={`Something went wrong processing this class. Contact support with meeting id: ${contentState.meetingId}`}
        />
      );
    }

    if (summaryHtml) {
      return (
        <ScrollView style={styles.summaryContainer} showsVerticalScrollIndicator={true}>
          <RenderHtml source={{ html: summaryHtml }} tagsStyles={tagsStyles} systemFonts={fonts} />
        </ScrollView>
      );
    }

    if (message) {
      return (
        <ScrollView style={styles.summaryContainer} showsVerticalScrollIndicator={true}>
          <Text>{message}</Text>
        </ScrollView>
      );
    }

    return <Text style={styles.noDataText}>No data available</Text>;
  };

  return (
    <AccordionBase
      title={title}
      icon={<Icon type="document" />}
      rightControls={reviewButton}
      aboveContent={
        showPills && (
          <TogglePillSet
            options={pillOptions}
            selectedValue={effectiveBlockId}
            onSelect={setSelectedBlockId}
            size="small"
            accessibilityLabel="Select subject"
          />
        )
      }
      content={getContent()}
      copyText={getCopyText()}
      isCollapsed={isCollapsed}
      onToggleCollapse={handleToggleCollapse}
      state={accordionState}
      autoExpand={false}
      onReviewButtonClick={handleReviewButtonClick}
      noContentBorder={!!error}
    />
  );
};

const styles = StyleSheet.create({
  summaryContainer: {
    padding: theme.spacing.medium,
    maxHeight: 360,
    flex: 1,
  },
  loadingContainer: {
    height: 400,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    color: theme.colors.darkGray,
    textAlign: 'center',
    padding: theme.spacing.large,
  },
  reviewButton: {
    borderRadius: theme.borderRadius.xlarge,
  },
  reviewButtonText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
});
